import { useState, useMemo } from 'react';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  MapPin,
  X,
  ExternalLink,
  Building2,
  Search,
  Info,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/core/components/ui/card';
import { Button } from '@/core/components/ui/button';
import { Input } from '@/core/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/core/components/ui/table';
import { environmentConfig } from '@/core/api-config';
import type { PublishLocationsResult } from '../../types/connection-status-types';
import type { ActorError, ActorWarning } from '../../types/api-types';
import { cn } from '@/core/lib/utils';
import { GRID_STYLES } from '@/core/components/ui/grid-styles';

/**
 * Highlights matching text with yellow background
 */
function HighlightedText({ text, query }: { text: string; query: string }) {
  if (!query.trim()) return <>{text}</>;

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase().trim();
  const index = lowerText.indexOf(lowerQuery);

  if (index === -1) return <>{text}</>;

  const before = text.slice(0, index);
  const match = text.slice(index, index + lowerQuery.length);
  const after = text.slice(index + lowerQuery.length);

  return (
    <>
      {before}
      <span className="bg-yellow-200 px-0.5 rounded">{match}</span>
      {after}
    </>
  );
}

// Deep link to machine in Nayax Core (environment-aware)
const getMachineDeepLink = (machineId: number): string => {
  const portalUrl = environmentConfig.getPortalUrl();
  return `${portalUrl}/core/public/f?model=operations/machine#${machineId}`;
};

// Deep link to actor (site/location) in Nayax Core (environment-aware)
const getActorDeepLink = (actorId: number): string => {
  const portalUrl = environmentConfig.getPortalUrl();
  return `${portalUrl}/core/public/f?model=administration/Actors#${actorId}`;
};

interface PublishLocationsResultPanelProps {
  result: PublishLocationsResult;
  onClose?: () => void;
}

export function PublishLocationsResultPanel({ result, onClose }: PublishLocationsResultPanelProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showErrors, setShowErrors] = useState(true);
  const [showWarnings, setShowWarnings] = useState(true);
  const [expandedActors, setExpandedActors] = useState<Set<number>>(new Set());
  const [showPublishedLocations, setShowPublishedLocations] = useState(false); // Collapsed by default
  const [actorSearchQuery, setActorSearchQuery] = useState('');

  const actorErrors = result.response.ocpiSummaryErrors?.actorErrors ?? [];
  const warnings = result.response.ocpiSummaryErrors?.warnings ?? [];
  const publishedLocations = result.response.publishedLocations ?? [];
  const hasErrors = actorErrors.length > 0 || (result.errors && result.errors.length > 0);
  const hasWarnings = warnings.length > 0;
  const hasPublishedLocations = publishedLocations.length > 0;

  // Filter and sort published locations by search query
  const filteredPublishedLocations = useMemo(() => {
    let filtered = publishedLocations;

    if (actorSearchQuery.trim()) {
      const query = actorSearchQuery.toLowerCase().trim();
      filtered = publishedLocations.filter(
        (loc) =>
          (loc.actorName?.toLowerCase().includes(query)) ||
          loc.actorId.toString().includes(query)
      );
    }

    // Sort by actor name (alphabetically), with null names at the end
    return [...filtered].sort((a, b) => {
      const nameA = a.actorName ?? '';
      const nameB = b.actorName ?? '';
      if (!nameA && nameB) return 1;
      if (nameA && !nameB) return -1;
      return nameA.localeCompare(nameB);
    });
  }, [publishedLocations, actorSearchQuery]);

  const toggleActor = (actorId: number) => {
    setExpandedActors((prev) => {
      const next = new Set(prev);
      if (next.has(actorId)) {
        next.delete(actorId);
      } else {
        next.add(actorId);
      }
      return next;
    });
  };

  return (
    <Card className={result.isSuccess ? 'border-green-200' : 'border-red-200'}>
      <CardHeader className={`${result.isSuccess ? 'bg-green-50' : 'bg-red-50'} cursor-pointer`}>
        <div className="flex items-center justify-between">
          <div
            className="flex items-center gap-3 flex-1"
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            {isCollapsed ? (
              <ChevronRight className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-500" />
            )}
            {result.isSuccess ? (
              <CheckCircle className="h-6 w-6 text-green-600" />
            ) : (
              <XCircle className="h-6 w-6 text-red-600" />
            )}
            <div>
              <CardTitle
                className={`text-lg ${result.isSuccess ? 'text-green-800' : 'text-red-800'}`}
              >
                <MapPin className="h-5 w-5 inline mr-2" />
                Publish Locations Result
                <span className="ml-2 text-sm font-normal">
                  ({publishedLocations.length > 0 ? publishedLocations.length : result.response.messageIds?.length ?? 0} published, {result.failedCount} failed)
                </span>
              </CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                {new Date(result.timestamp).toLocaleString()}
              </p>
            </div>
          </div>
          {onClose && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>

      {!isCollapsed && (
        <CardContent className="pt-4 space-y-4">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-green-700">
              {publishedLocations.length > 0 ? publishedLocations.length : result.response.messageIds?.length ?? 0}
            </p>
            <p className="text-sm text-green-600">Locations Published</p>
          </div>
          <div className="bg-red-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-red-700">{result.failedCount}</p>
            <p className="text-sm text-red-600">Machines Failed</p>
          </div>
          <div className="bg-orange-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-orange-700">{actorErrors.length}</p>
            <p className="text-sm text-orange-600">Actors with Errors</p>
          </div>
          <div className="bg-yellow-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-yellow-700">{warnings.length}</p>
            <p className="text-sm text-yellow-600">Warnings</p>
          </div>
        </div>

        {/* Warning when no locations were published */}
        {publishedLocations.length === 0 && result.failedCount === 0 && !hasErrors && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-blue-800">No Locations Published</h4>
                <p className="text-sm text-blue-700 mt-1">
                  The publish operation completed successfully, but no locations were published.
                  This may indicate that:
                </p>
                <ul className="text-sm text-blue-700 mt-2 list-disc list-inside space-y-1">
                  <li>No locations are configured for OCPI publishing</li>
                  <li>All locations may have been filtered out due to missing data</li>
                  <li>The selected operator has no active EV charging stations</li>
                </ul>
                <p className="text-sm text-blue-700 mt-2">
                  Please verify your location configuration in the Nayax Core portal.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* General Errors */}
        {result.errors && result.errors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="font-medium text-red-800 mb-2">General Errors</h4>
            <ul className="space-y-1">
              {result.errors.map((error, index) => (
                <li key={index} className="text-sm text-red-700 flex items-start gap-2">
                  <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  {error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Published Locations Table */}
        {hasPublishedLocations && (
          <div className="border rounded-lg">
            <Button
              variant="ghost"
              className="w-full justify-between p-4 h-auto"
              onClick={() => setShowPublishedLocations(!showPublishedLocations)}
            >
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-green-600" />
                <span className="font-medium">
                  Published Locations ({publishedLocations.length} actors)
                </span>
              </div>
              {showPublishedLocations ? (
                <ChevronDown className="h-5 w-5" />
              ) : (
                <ChevronRight className="h-5 w-5" />
              )}
            </Button>

            {showPublishedLocations && (
              <div className="border-t">
                {/* Search input */}
                <div className="p-3 border-b bg-gray-50">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search by actor name or ID..."
                      value={actorSearchQuery}
                      onChange={(e) => setActorSearchQuery(e.target.value)}
                      className="pl-9 h-9"
                    />
                  </div>
                  {actorSearchQuery && (
                    <p className="text-xs text-gray-500 mt-2">
                      Showing {filteredPublishedLocations.length} of {publishedLocations.length} actors
                    </p>
                  )}
                </div>
                <Table>
                  <TableHeader className={GRID_STYLES.header.background}>
                    <TableRow className={cn(GRID_STYLES.header.background, 'border-b', GRID_STYLES.border)}>
                      <TableHead className={GRID_STYLES.header.text}>Actor</TableHead>
                      <TableHead className={cn('text-center', GRID_STYLES.header.text)}>Machines</TableHead>
                      <TableHead className={GRID_STYLES.header.text}>Message ID</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPublishedLocations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-gray-500 py-8">
                          No actors found matching "{actorSearchQuery}"
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredPublishedLocations.map((loc) => {
                        const actorDisplay = loc.actorName ?? `Actor ${loc.actorId}`;
                        return (
                          <TableRow key={loc.messageId} className={GRID_STYLES.row.hover}>
                            <TableCell>
                              <a
                                href={getActorDeepLink(loc.actorId)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
                              >
                                <HighlightedText text={actorDisplay} query={actorSearchQuery} />
                                <ExternalLink className="h-3 w-3" />
                              </a>
                              {loc.actorName && (
                                <span className="text-xs text-gray-500 ml-1">
                                  (ID: <HighlightedText text={loc.actorId.toString()} query={actorSearchQuery} />)
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                                {loc.machineCount} machine{loc.machineCount !== 1 ? 's' : ''}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="text-xs font-mono text-gray-500">{loc.messageId}</span>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        {/* Actor Errors Section */}
        {hasErrors && actorErrors.length > 0 && (
          <div className="border rounded-lg">
            <Button
              variant="ghost"
              className="w-full justify-between p-4 h-auto"
              onClick={() => setShowErrors(!showErrors)}
            >
              <div className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-600" />
                <span className="font-medium">
                  Errors by Actor ({actorErrors.length} actors affected)
                </span>
              </div>
              {showErrors ? (
                <ChevronDown className="h-5 w-5" />
              ) : (
                <ChevronRight className="h-5 w-5" />
              )}
            </Button>

            {showErrors && (
              <div className="border-t p-4 space-y-3">
                {actorErrors.map((actorError) => (
                  <ActorErrorItem
                    key={actorError.actorId}
                    error={actorError}
                    isExpanded={expandedActors.has(actorError.actorId)}
                    onToggle={() => toggleActor(actorError.actorId)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Warnings Section */}
        {hasWarnings && (
          <div className="border rounded-lg">
            <Button
              variant="ghost"
              className="w-full justify-between p-4 h-auto"
              onClick={() => setShowWarnings(!showWarnings)}
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
                <span className="font-medium">Warnings ({warnings.length} actors)</span>
              </div>
              {showWarnings ? (
                <ChevronDown className="h-5 w-5" />
              ) : (
                <ChevronRight className="h-5 w-5" />
              )}
            </Button>

            {showWarnings && (
              <div className="border-t p-4 space-y-3">
                {warnings.map((warning) => (
                  <ActorWarningItem
                    key={warning.actorId}
                    warning={warning}
                    isExpanded={expandedActors.has(warning.actorId + 10000)} // offset to avoid collision with error actors
                    onToggle={() => toggleActor(warning.actorId + 10000)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Success Machine IDs */}
        {result.successMachineIds.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-medium text-green-800 mb-2">
              Successfully Published Machines ({result.successMachineIds.length})
            </h4>
            <div className="flex flex-wrap gap-2">
              {result.successMachineIds.slice(0, 20).map((machineId) => (
                <span
                  key={machineId}
                  className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded"
                >
                  {machineId}
                </span>
              ))}
              {result.successMachineIds.length > 20 && (
                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                  +{result.successMachineIds.length - 20} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Failed Machine IDs */}
        {result.failedMachineIds.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="font-medium text-red-800 mb-2">
              Failed Machines ({result.failedMachineIds.length})
            </h4>
            <div className="flex flex-wrap gap-2">
              {result.failedMachineIds.slice(0, 20).map((machineId) => (
                <a
                  key={machineId}
                  href={getMachineDeepLink(machineId)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded hover:bg-red-200 inline-flex items-center gap-1"
                >
                  {machineId}
                  <ExternalLink className="h-3 w-3" />
                </a>
              ))}
              {result.failedMachineIds.length > 20 && (
                <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded">
                  +{result.failedMachineIds.length - 20} more
                </span>
              )}
            </div>
          </div>
        )}
        </CardContent>
      )}
    </Card>
  );
}

function ActorErrorItem({
  error,
  isExpanded,
  onToggle,
}: {
  error: ActorError;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const totalMachineErrors = error.machineErrors?.length ?? 0;
  const actorDisplayName = error.actorName ?? `Actor ${error.actorId}`;

  return (
    <div className="bg-red-50 border border-red-200 rounded-lg">
      <Button
        variant="ghost"
        className="w-full justify-between p-3 h-auto hover:bg-red-100"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
          <a
            href={getActorDeepLink(error.actorId)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="font-medium text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
          >
            {actorDisplayName}
            <ExternalLink className="h-3 w-3" />
          </a>
          {error.actorName && (
            <span className="text-xs text-gray-500">(ID: {error.actorId})</span>
          )}
          {totalMachineErrors > 0 && (
            <span className="text-xs bg-red-200 text-red-800 px-2 py-0.5 rounded">
              {totalMachineErrors} machine(s)
            </span>
          )}
        </div>
      </Button>

      {isExpanded && (
        <div className="border-t border-red-200 p-3 space-y-2">
          {/* Actor-level errors */}
          {error.errors && error.errors.length > 0 && (
            <div>
              <p className="text-xs font-medium text-red-700 mb-1">Actor Errors:</p>
              <ul className="space-y-1">
                {error.errors.map((err, index) => (
                  <li key={index} className="text-sm text-red-600 pl-4">
                    • {err}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Machine-level errors */}
          {error.machineErrors && error.machineErrors.length > 0 && (
            <div>
              <p className="text-xs font-medium text-red-700 mb-1">Machine Errors:</p>
              {error.machineErrors.map((machineError) => {
                const machineDisplayName = machineError.machineName ?? `Machine ${machineError.machineId}`;
                return (
                  <div
                    key={machineError.machineId}
                    className="ml-4 mb-2 bg-white p-2 rounded border border-red-100"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-red-800">
                        {machineDisplayName}
                        {machineError.machineName && (
                          <span className="text-xs text-gray-500 ml-1">(ID: {machineError.machineId})</span>
                        )}
                      </p>
                      <a
                        href={getMachineDeepLink(machineError.machineId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        Open in Core
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                    <ul className="mt-1">
                      {machineError.errors.map((err, index) => (
                        <li key={index} className="text-sm text-red-600 pl-2">
                          • {err}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ActorWarningItem({
  warning,
  isExpanded,
  onToggle,
}: {
  warning: ActorWarning;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const totalMachineWarnings = warning.machineWarnings?.length ?? 0;
  const actorDisplayName = warning.actorName ?? `Actor ${warning.actorId}`;

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg">
      <Button
        variant="ghost"
        className="w-full justify-between p-3 h-auto hover:bg-yellow-100"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
          <a
            href={getActorDeepLink(warning.actorId)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="font-medium text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
          >
            {actorDisplayName}
            <ExternalLink className="h-3 w-3" />
          </a>
          {warning.actorName && (
            <span className="text-xs text-gray-500">(ID: {warning.actorId})</span>
          )}
          {totalMachineWarnings > 0 && (
            <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded">
              {totalMachineWarnings} machine(s)
            </span>
          )}
        </div>
      </Button>

      {isExpanded && (
        <div className="border-t border-yellow-200 p-3 space-y-2">
          {/* Actor-level warnings */}
          {warning.warnings && warning.warnings.length > 0 && (
            <div>
              <p className="text-xs font-medium text-yellow-700 mb-1">Actor Warnings:</p>
              <ul className="space-y-1">
                {warning.warnings.map((warn, index) => (
                  <li key={index} className="text-sm text-yellow-600 pl-4">
                    • {warn}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Machine-level warnings */}
          {warning.machineWarnings && warning.machineWarnings.length > 0 && (
            <div>
              <p className="text-xs font-medium text-yellow-700 mb-1">Machine Warnings:</p>
              {warning.machineWarnings.map((machineWarning) => {
                const machineDisplayName = machineWarning.machineName ?? `Machine ${machineWarning.machineId}`;
                return (
                  <div
                    key={machineWarning.machineId}
                    className="ml-4 mb-2 bg-white p-2 rounded border border-yellow-100"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-yellow-800">
                        {machineDisplayName}
                        {machineWarning.machineName && (
                          <span className="text-xs text-gray-500 ml-1">(ID: {machineWarning.machineId})</span>
                        )}
                      </p>
                      <a
                        href={getMachineDeepLink(machineWarning.machineId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        Open in Core
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                    <ul className="mt-1">
                      {machineWarning.warnings.map((warn, index) => (
                        <li key={index} className="text-sm text-yellow-600 pl-2">
                          • {warn}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
